/**
 * A wrapper around FileViewer that implements the methods to view emails from Outlook via Microsoft Graph
 */

import React, { useCallback, useEffect } from 'react'
import { FileViewerDialog, FileViewerMenuAction } from './FileViewerDialog'
import { FileViewerContents } from './FileViewerContents'
import azEmailService from '../../../services/azureGraphEmailService'
import { EmailAttachmentsSidebar } from './EmailAttachmentsSidebar'
import toast from 'react-hot-toast'
import { downloadBuffer } from '../../../models/file'
import EmailAttachmentModel from '../../../models/Attachment'
import { observer } from 'mobx-react-lite'

export interface MSEmailFileViewerProps {
  emailId: string
  attachment: EmailAttachmentModel
  open: boolean
  onClose: () => void
  attachmentsList: EmailAttachmentModel[]
  onOpenAttachmentFromList: (id: string) => void
}

function MSEmailFileViewer({ emailId, open, onClose, attachment, attachmentsList, onOpenAttachmentFromList }: MSEmailFileViewerProps) {
  const [fileContents, setFileContents] = React.useState<ArrayBuffer | null>(null)
  const [loadingProgress, setLoadingProgress] = React.useState(0)

  const handleSelectAction = useCallback(async (action: FileViewerMenuAction) => {
    switch (action) {
      case FileViewerMenuAction.Download:
        try {
          const fileBuf = await azEmailService.retrieveEmailAttachmentContent({
            emailId,
            attachmentId: attachment.id,
            mailboxEmailAddress: attachment.mailboxEmailAddress
          })

          downloadBuffer(fileBuf!, {
            name: attachment.name,
            type: attachment.contentType
          })
        } catch (e) {
          toast.error(`Failed to download file. ${e}`)
        }

        break
    }
  }, [])

  const loadContents = async () => {
    const buf = await azEmailService.retrieveEmailAttachmentContent(
      {
        emailId,
        attachmentId: attachment.id,
        mailboxEmailAddress: attachment.mailboxEmailAddress
      },
      (p) => setLoadingProgress(p)
    )

    setFileContents(buf)
  }

  useEffect(() => {
    if (open) {
      // attachment.loadContent((p) => {
      //   console.log('set progress', p)
      //   setLoadingProgress(p)
      // })

      loadContents()
    }
  }, [open, attachment.binaryContent, attachment.id])

  return (
    <FileViewerDialog open={open} onClose={onClose}>
      <FileViewerContents
        loadingProgress={loadingProgress}
        fileData={attachment.binaryContent ?? fileContents}
        fileMeta={{
          name: attachment.name,
          type: attachment.contentType
        }}
      />
      <EmailAttachmentsSidebar
        fileName={attachment.name}
        onSelectAction={handleSelectAction}
        onClose={onClose}
        attachmentsList={attachmentsList}
        onOpenAttachmentFromList={onOpenAttachmentFromList}
      />
    </FileViewerDialog>
  )
}

export default observer(MSEmailFileViewer)
